import { User } from '@overtur-monorepo/keying-user'
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop'
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core'
import { FormControl } from '@angular/forms'
import { Subject, Subscription, combineLatest, timer } from 'rxjs'
import { take, takeUntil } from 'rxjs/operators'
import { KeySystemStatus, KeySystemStatuses } from '../../constants/keySystem-statuses'
import { KeySystem, Structure } from '../../models/dev.test.models'
import { KeyingAuthService } from '../../services/keying.auth.service'
import { KeyingStructureService } from '../../services/keying.structure.service'
import { KeyingSystemService } from '../../services/keying.system.service'
import { StructureSearchParamsService } from '../../services/structure-search-params.service'
import { LoadingService } from '../../services/loading.service'
import { TokenizedMessageLoadingService } from '../../services/tokenized-message-loading/tokenized-message-loading.service'
import { environment } from '../../../environments/environment'
import { MatChipInputEvent } from '@angular/material/chips'
import { RPAService } from '../../services/rpa.service'
import { ErrorService } from '../../services/error.service'

export interface RPA {
  authorizationNumber: string;
  expirationDate: string;
  orderNumber: string;
}

@Component({
    selector: 'app-kanban-board',
    templateUrl: './kanban-board.component.html',
    styleUrls: ['./kanban-board.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class KanbanBoardComponent implements OnInit, OnChanges, OnDestroy {
  destroy$ = new Subject()
  users: User[]
  user: User

  structures: Structure[]
  doing: Structure[] = []
  mecs: Structure[] = []
  @Input() keySystem: KeySystem

  filteredDoing: Structure[] = []
  filteredMecs: Structure[] = []

  keySystemStatus = KeySystemStatus

  userFilterForm = new FormControl()
  usersNamesList = []
  usersToFilterBy
  selections = []
  selectedUsers = []
  allSelected = false
  apiSubscription: Subscription | null = null
  interval = 30
  rpas = []
  rpaPlaceholder = 'Enter RPA number'

  todayDate: Date = new Date()

  redPill = false
  greenPill = false

  constructor(
    private keyingAuthService: KeyingAuthService,
    private keyingSystemService: KeyingSystemService,
    private keyingStructureService: KeyingStructureService,
    private structureSearchParamsService: StructureSearchParamsService,
    private rpaService: RPAService,
    private errorService: ErrorService,
    private loadingService: LoadingService,
    private tokenizedLoadingService: TokenizedMessageLoadingService,
    private cdr: ChangeDetectorRef
  ) {}

  /* istanbul ignore next */
  ngOnInit(): void {
    combineLatest([this.keyingAuthService.users$, this.keyingAuthService.user$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([users, user]: [User[], any]) => {
        if (!users) {
          return
        }
        this.users = users
        this.users.sort((a, b) => a.firstName.localeCompare(b.firstName))

        this.users.forEach((user) => {
          this.usersNamesList.push({ name: user.firstName + ' ' + user.lastName, id: user.id })
        })

        //Removing System User as a filter option
        this.usersToFilterBy = this.users.filter((user) => user.id !== 0)

        this.keyingAuthService.keyingUser$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
          this.user = user
          this.cdr.markForCheck()
        })

        this.selections = this.users.filter((u) => user.name.includes(u.firstName) && user.name.includes(u.lastName))
        this.userFilterForm.setValue(this.selections, { emitEvent: false })
        this.filterLists(false)
        this.cdr.markForCheck()

        this.keyingStructureService.structures$.pipe(takeUntil(this.destroy$)).subscribe((structures: Structure[]) => {
          if (!structures.length) {
            return
          }
          this.updateLists(structures)
          this.shouldScheduleRefresh(structures)
          this.cdr.detectChanges()
          this.tokenizedLoadingService.stopLoading('structures-by-userid')
        })
      })
  }

  /* istanbul ignore next */
  ngOnChanges(changes: SimpleChanges) {
    this.filterLists(false)
  }

  /* istanbul ignore next */
  ngOnDestroy() {
    this.destroy$.next(true)
    this.destroy$.complete()
    if (this.apiSubscription) {
      this.apiSubscription.unsubscribe()
    }
  }

  /* istanbul ignore next */
  removeRPALookup(rpa: RPA): void {
    const index = this.rpas.indexOf(rpa);
    if (index >= 0) {
      this.rpas.splice(index, 1);
      this.rpaPlaceholder = 'Enter RPA number';
      this.cdr.markForCheck();
    }
  }

/* istanbul ignore next */
  addRPALookup(event: MatChipInputEvent): void {
    this.rpas = []
    const input = event.input;
    const value = event.value;
    // check RPA status
    this.loadingService.set({ isLoading: true, message: 'Looking up RPA' })
    this.rpaService.getRPAStatus(value).pipe(take(1)).subscribe((res) => {
      //check if response is empty
      if (JSON.stringify(res) !== '{}') {
        this.rpas.push(res);
        this.rpaPlaceholder = '';
        const expirationDate: string = this.rpas[0].expirationDate;
        const expiration = new Date(expirationDate);
        // Conditional checks for redPill and greenPill after pushing to this.rpas[]
        if (this.rpas[0].orderNumber && !this.rpas[0].expirationDate) {
          this.redPill = true;
          this.greenPill = false;
        } else if (!this.rpas[0].orderNumber && !this.rpas[0].expirationDate) {
          this.redPill = false;
          this.greenPill = true;
        } else if (this.rpas[0].expirationDate && expiration >= this.todayDate) {
          this.redPill = false;
          this.greenPill = true;
        } else if (this.rpas[0].expirationDate && expiration < this.todayDate) {
          this.redPill = true;
          this.greenPill = false;
        }
      } else {
        this.errorService.throw({ error: { message: 'RPA not found' } });
      }
      this.loadingService.set({ isLoading: false, message: '' })
      this.cdr.markForCheck()

    }, (error) => {
      this.loadingService.set({ isLoading: false, message: '' })
      this.errorService.throw({ error: error });
      this.cdr.markForCheck()
    })

    // Reset the input value
    if (input) {
      input.value = '';
    }
  }

  updateLists(structures) {
    this.structures = structures

    this.structures = this.structures.filter((element) =>
      element.keySystems.some((subElement) => subElement.isRejected !== true)
    )
    this.doing = []
    this.mecs = []

    structures.map((s) => {
      s.showSystems = true
      this.status(s)

      const doing = s.keySystems.filter((k: KeySystem) => k.statusId >= 1 && k.statusId <= 5)
      const mecs = s.keySystems.filter((k: KeySystem) => k.statusId >= 6 && k.statusId <= 12)

      if (doing.length > 0) {
        this.doing.push(s)
      } else if (mecs.length > 0) {
        this.mecs.push(s)
      }
    })

    /* istanbul ignore next */
    this.doing.sort((a, b) => {
      if (!a.keySystems[0].modifiedOn) return 1
      if (!b.keySystems[0].modifiedOn) return -1
      return new Date(b.keySystems[0].modifiedOn).getTime() - new Date(a.keySystems[0].modifiedOn).getTime()
    })
    /* istanbul ignore next */
    this.mecs.sort((a, b) => {
      if (!a.keySystems[0].modifiedOn) return 1
      if (!b.keySystems[0].modifiedOn) return -1
      return new Date(b.keySystems[0].modifiedOn).getTime() - new Date(a.keySystems[0].modifiedOn).getTime()
    })
    this.filteredDoing = this.doing
    this.filteredMecs = this.mecs
  }

  /* istanbul ignore next */
  select() {
    this.selections = this.userFilterForm.value || []
    this.filterLists(false)
  }

  /* istanbul ignore next */
  toggleSelectAll() {
    this.allSelected = !this.allSelected
    if (this.allSelected) {
      this.selections = this.usersToFilterBy
      this.userFilterForm.setValue(this.usersToFilterBy, { emitEvent: false })
    } else {
      this.selections = []
      this.userFilterForm.setValue([], { emitEvent: false })
    }
    this.filterLists(false)
  }

  /* istanbul ignore next */
  filterLists(isSilent) {
    this.selectedUsers = this.selections
    if (this.selections?.length) {
      const ids = this.selections.map((s) => (s = s.id))
      const params = ids.map((i) => (i = { userids: i }))
      this.keyingStructureService.mapStructuresByUserId(params, isSilent)
    } else {
      this.selections = []
      this.filteredDoing = []
      this.filteredMecs = []
    }
  }

  scheduleRefreshStructures() {
    // timer(0, 30000) fires immediately, then every 30 seconds
    // interval(30000) waits 30 seconds before first fire
    const schedule = timer(0, this.interval * 1000)
    this.apiSubscription = schedule.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.filterLists(true)
    })
  }

  /* istanbul ignore next */
  shouldScheduleRefresh(structures) {
    if (environment.flags.autoReload) {
      let shouldSchedule = false
      structures.forEach((structure) => {
        structure.shouldGetMecsStatus = false
        structure.keySystems.forEach((keySystem) => {
          if (
            keySystem.statusId === this.keySystemStatus.InPinningGenerationQueue ||
            keySystem.statusId === this.keySystemStatus.OfflinePinningGenerationInProgress ||
            keySystem.statusId === this.keySystemStatus.InMECSQueue ||
            keySystem.statusId === this.keySystemStatus.MECSInProgress ||
            keySystem.statusId === this.keySystemStatus.InPublishingQueue
          ) {
            shouldSchedule = true
          }
          if (keySystem.statusId === this.keySystemStatus.MECSInProgress) {
            // Get MECS status percentage for the in progress key system
            structure.shouldGetMecsStatus = true
          } else if (keySystem.statusId === this.keySystemStatus.MECSCompletedReadyToPublish) {
            keySystem.mecsProgressPercentage = 100
            structure.shouldGetMecsStatus = false
          } else {
            keySystem.mecsProgressPercentage = 0
            structure.shouldGetMecsStatus = false
          }
        })
        if (structure.shouldGetMecsStatus) {
          this.getMECSStatus(structure)
        }
      })

      if (!this.apiSubscription && shouldSchedule) {
        this.scheduleRefreshStructures()
      } else if (this.apiSubscription && !shouldSchedule) {
        this.apiSubscription.unsubscribe()
      }
    }
  }

  /* istanbul ignore next */
  getMECSStatus(structure) {
    this.keyingSystemService.getMECSStatus(structure.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res) {
          structure.keySystems.forEach((keySystem) => {
            let matchedKeySystem = res.filter((x) => {
              return x.keySystemId === keySystem.id
            })
            if (matchedKeySystem.length > 0) {
              keySystem.mecsProgressPercentage = matchedKeySystem[0].percentageCompleted
            }
          })
          this.cdr.markForCheck()
        }
      })
  }

  /* istanbul ignore next */
  drop(event: CdkDragDrop<any>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data.items, event.previousIndex, event.currentIndex)
    }
  }

  status(structure) {
    if (structure?.keySystems[0] && !structure?.keySystems[0]?.statusId) {
      structure.keySystems[0].statusId = 1
    }

    if (structure?.keySystems[0]) {
      structure?.keySystems.forEach((element, index) => {
        Object.keys(KeySystemStatuses).forEach((x) => {
          if (x == structure.keySystems[index].statusId.toString()) {
            structure.keySystems[index].status = KeySystemStatuses[x]
          }
        })
      })
    }
  }
}
